import {
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    inject,
    InjectionToken,
    input,
    linkedSignal,
    Provider,
    untracked,
} from '@angular/core';

export type TablerIconMap = Record<string, string>;

export interface TablerIconOptions {
    icons: TablerIconMap;
    ignoreWarnings?: boolean;
}

export const TABLER_ICON_OPTIONS = new InjectionToken<TablerIconOptions[]>(
    'TABLER_ICON_OPTIONS'
);

function upperCamelCase(value: string | undefined): string | undefined {
    if (!value) {
        return value;
    }

    return value
        .toLowerCase()
        .split(/[-_]/g)
        .filter(Boolean)
        .map(
            (part) => part.charAt(0).toUpperCase() + part.slice(1)
        )
        .join('');
}

@Component({
    selector: 'i-tabler, tabler-icon',
    standalone: true,
    template: `
        <ng-content/>`,
    styles: [
        `
          :host {
            display: inline-block;
            width: 24px;
            height: 24px;
            fill: none;
            stroke: currentColor;
            stroke-width: 2px;
            stroke-linecap: round;
            stroke-linejoin: round;
          }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TablerIconComponent {
    readonly name = input.required<string>();
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly options = inject(TABLER_ICON_OPTIONS, {optional: true}) ?? [];
    private readonly svg = linkedSignal(() => this.resolveIcon(this.name()));

    private readonly setNativeSvg = effect(() => {
        const content = this.svg() ?? '';
        untracked(() => {
            this.elementRef.nativeElement.innerHTML = content;
        });
    });

    private resolveIcon(iconName: string | undefined): string | undefined {
        if (!iconName) {
            return undefined;
        }

        if (!this.options.length) {
            return undefined;
        }

        const icons = Object.assign({}, ...this.options.map((option) => option.icons));
        const lookupKey = `Icon${upperCamelCase(iconName)}`;
        const svg = icons[lookupKey];

        if (!svg && !this.options.some((option) => option.ignoreWarnings)) {
            console.warn(
                `Tabler icon not found: ${iconName}. Ensure it is included in the local icon set.`
            );
        }

        return svg;
    }
}

export function provideTablerIcons(
    icons: TablerIconMap,
    options?: Omit<TablerIconOptions, 'icons'>
): Provider[] {
    return [
        {
            provide: TABLER_ICON_OPTIONS,
            useValue: {
                icons,
                ...options,
            },
            multi: true,
        },
    ];
}
